import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  Flex,
  useColorModeValue,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Tag,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaPlus, FaSearch, FaUsers, FaEye, FaEdit } from "react-icons/fa";
import { getRole, getToken, canManageData } from "utils/auth";
import config from "config";

const Families = () => {
  const [families, setFamilies] = useState([]);
  const [filteredFamilies, setFilteredFamilies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const tableHeaderBg = useColorModeValue("slate.50", "navy.800");

  useEffect(() => {
    const token = getToken();
    axios
      .get(`${config.API_URL}/individuals/families?with_members=true`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((response) => {
        setFamilies(response.data);
        setFilteredFamilies(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching families catalog:", error);
        setLoading(false);
      });
  }, []);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term) {
      setFilteredFamilies(families);
    } else {
      setFilteredFamilies(
        families.filter((fam) =>
          fam.family_code?.toLowerCase().includes(term.toLowerCase())
        )
      );
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="teal.500" thickness="4px" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" w="100%">
      <Box
        p={{ base: "14px", sm: "20px", md: "28px" }}
        bg={cardBg}
        borderRadius="24px"
        border="1px solid"
        borderColor={cardBorder}
        shadow="sm"
      >
        <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} mb="24px" gap="16px">
          <Box>
            <Heading size="lg" color={textColor} fontWeight="800" letterSpacing="-0.02em">
              Families
            </Heading>
            <Text fontSize="sm" color={subtitleColor} mt="4px">
              Patients families data
            </Text>
          </Box>
          {canManageData() && (
            <Button
              as={Link}
              to="/txd/families/add"
              colorScheme="teal"
              leftIcon={<FaPlus />}
              borderRadius="12px"
              px="20px"
              w={{ base: "100%", md: "auto" }}
            >
              Create New Family
            </Button>
          )}
        </Flex>

        {/* Filter input */}
        <Box maxW={{ base: "100%", sm: "400px" }} mb="24px">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by Family Code..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              borderRadius="12px"
              bg={useColorModeValue("slate.50", "navy.800")}
            />
          </InputGroup>
        </Box>

        {/* Families Table */}
        {filteredFamilies.length > 0 ? (
          <Box overflowX="auto" borderRadius="16px" border="1px solid" borderColor={cardBorder}>
            <Table variant="simple" color={textColor}>
              <Thead bg={tableHeaderBg}>
                <Tr>
                  <Th py="16px" fontSize="xs" fontWeight="700" color={useColorModeValue("slate.600", "slate.300")}>FAMILY CODE</Th>
                  <Th py="16px" fontSize="xs" fontWeight="700" color={useColorModeValue("slate.600", "slate.300")}>REGISTERED MEMBERS</Th>
                  <Th py="16px" fontSize="xs" fontWeight="700" color={useColorModeValue("slate.600", "slate.300")}>ACTIONS</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredFamilies.map((family) => (
                  <Tr key={family.family_code} _hover={{ bg: useColorModeValue("slate.50", "whiteAlpha.05") }}>
                    <Td py="16px">
                      <Tag size="md" colorScheme="teal" borderRadius="full" px="14px" py="4px" fontWeight="700">
                        {family.family_code}
                      </Tag>
                    </Td>
                    <Td py="16px">
                      <HStack spacing="6px">
                        <FaUsers color="#0d9488" />
                        <Text fontSize="sm" fontWeight="700" color={textColor}>
                          {family.members_count || 0} members
                        </Text>
                      </HStack>
                    </Td>
                    <Td py="16px">
                      <HStack spacing="10px">
                        <Button
                          as={Link}
                          to={`/txd/families/members/${family.family_code}`}
                          size="sm"
                          colorScheme="teal"
                          variant="solid"
                          leftIcon={<FaEye />}
                          borderRadius="10px"
                        >
                          View Members
                        </Button>
                        {canManageData() && (
                          <Button
                            as={Link}
                            to={`/txd/families/edit/${family.family_code}`}
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            leftIcon={<FaEdit />}
                            borderRadius="10px"
                          >
                            Edit Family
                          </Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Text color={subtitleColor} py="30px" textAlign="center">
            No family records found.
          </Text>
        )}
      </Box>
    </Flex>
  );
};

export default Families;

